import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { defineFormPage, useFormPage } from '@repo/form';
import { FormActions } from '@repo/web-form';
import { FormPageLayout } from '../../../../components/layout/FormPageLayout';
import { Input, FormField } from '../../../../components/ui/FormField';
import { useToast } from '../../../../components/ui/Toast';
import { useHolidays, useCreateHoliday } from '../../../../features/settings/network/holidays.api';
import { z } from 'zod';
import { Banner } from '../../../../components/ui/Banner';

export const Route = createFileRoute('/_auth/settings/holidays/new')({
  component: CreateHolidayPage,
});

const holidaySchema = z.object({
  name: z.string().min(1, 'Holiday name is required'),
  date: z.string().min(1, 'Holiday date is required'),
  isRecurring: z.boolean(),
});

type HolidayForm = z.infer<typeof holidaySchema>;

const holidayFormConfig = defineFormPage<HolidayForm>({
  resource: 'holidays',
  defaultValues: { name: '', date: '', isRecurring: false },
  schema: holidaySchema,
});

function CreateHolidayPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: holidays = [] } = useHolidays();
  const createHoliday = useCreateHoliday();

  const {
    form,
    isSubmitting,
    handleSubmit,
    hasDraft,
    restoreDraft,
    clearDraft,
    dismissDraft,
  } = useFormPage(holidayFormConfig, createHoliday, {
    routeId: 'settings/holidays/new',
    onBeforeSubmit: (value) => {
      if (holidays.some((h) => h.date === value.date)) {
        throw new Error('A holiday is already scheduled on this date.');
      }
      return value;
    },
    onSuccess: () => {
      toast({
        type: 'success',
        title: 'Success',
        description: 'Holiday recorded successfully. It will now be omitted from auction grace-period calculations.',
      });
      void navigate({ to: '/settings/holidays' });
    },
    onError: (err: { message?: string }) => {
      toast({
        type: 'error',
        title: 'Error',
        description: err.message || 'Failed to create holiday. Please try again.',
      });
    },
  });

  return (
    <FormPageLayout
      title="Add Company Holiday"
      subtitle="Schedule an institutional holiday. These dates are exempted from chit collection grace calculations."
      backTo="/settings/holidays"
      variant="card"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
            {hasDraft && (
              <Banner
                variant="info"
                title="Restore Draft"
                description="You have an unsaved draft from a previous session. Would you like to restore it?"
                action={{
                  label: 'Restore',
                  onClick: () => {
                    const draft = restoreDraft();
                    if (draft) {
                      Object.entries(draft).forEach(([key, val]) => {
                        form.setFieldValue(key as never, val as never);
                      });
                    }
                    dismissDraft();
                  },
                }}
                secondaryAction={{
                  label: 'Discard',
                  onClick: () => {
                    clearDraft();
                  },
                }}
              />
            )}

            <form.Field
              name="name"
              validators={{ onChange: ({ value }: { value: any }) => holidaySchema.shape.name.safeParse(value).success ? undefined : holidaySchema.shape.name.safeParse(value).error?.issues[0]?.message }}
              children={(field: any) => (
                <FormField label="Holiday Name" required>
                  <Input
                    placeholder="e.g. Durga Puja / Vijayadashami"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                    disabled={isSubmitting}
                  />
                </FormField>
              )}
            />

            <form.Field
              name="date"
              validators={{ onChange: ({ value }: { value: any }) => holidaySchema.shape.date.safeParse(value).success ? undefined : holidaySchema.shape.date.safeParse(value).error?.issues[0]?.message }}
              children={(field: any) => (
                <FormField label="Holiday Date" required>
                  <Input
                    type="date"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                    disabled={isSubmitting}
                  />
                </FormField>
              )}
            />

            <form.Field
              name="isRecurring"
              children={(field: any) => (
                <div className="flex items-center gap-2 pt-2 pb-2">
                  <Input
                    type="checkbox"
                    id="recurring"
                    checked={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.checked)}
                    className="w-4 h-4 accent-accent-500 cursor-pointer rounded"
                    disabled={isSubmitting}
                  />
                  <label htmlFor="recurring" className="text-xs text-text-secondary cursor-pointer select-none font-medium">
                    This is an annual recurring holiday (occurs on this same date every year)
                  </label>
                </div>
              )}
            />

            <FormActions onCancel={() => void navigate({ to: '/settings/holidays' })} isSubmitting={isSubmitting} submitLabel="Save Holiday" />
          </form>
    </FormPageLayout>
  );
}
